import React, { useState, useMemo, useCallback } from 'react';
import { Plus, Trash2, Settings, AlertTriangle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import { generateId } from '../../utils/helpers';

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const SUBJECT_COLORS = [
  'bg-blue-100 text-blue-800','bg-green-100 text-green-800',
  'bg-purple-100 text-purple-800','bg-orange-100 text-orange-800',
  'bg-pink-100 text-pink-800','bg-teal-100 text-teal-800',
  'bg-yellow-100 text-yellow-800','bg-red-100 text-red-800',
  'bg-cyan-100 text-cyan-800','bg-indigo-100 text-indigo-800',
];

const LS_KEY = 'tt_schedule_v2';
const DEFAULT_SCHEDULE = [
  { id:'p1', type:'period', label:'Period 1', startTime:'09:00', endTime:'09:45' },
  { id:'p2', type:'period', label:'Period 2', startTime:'09:45', endTime:'10:30' },
  { id:'b1', type:'break',  label:'Break',    startTime:'10:30', endTime:'10:45' },
  { id:'p3', type:'period', label:'Period 3', startTime:'10:45', endTime:'11:30' },
  { id:'p4', type:'period', label:'Period 4', startTime:'11:30', endTime:'12:15' },
  { id:'l1', type:'lunch',  label:'Lunch',    startTime:'12:15', endTime:'13:00' },
  { id:'p5', type:'period', label:'Period 5', startTime:'13:00', endTime:'13:45' },
  { id:'p6', type:'period', label:'Period 6', startTime:'13:45', endTime:'14:30' },
  { id:'p7', type:'period', label:'Period 7', startTime:'14:30', endTime:'15:15' },
  { id:'p8', type:'period', label:'Period 8', startTime:'15:15', endTime:'16:00' },
];

function assignPeriodNumbers(schedule) {
  let n = 1;
  return schedule.map(r => ({ ...r, number: r.type === 'period' ? n++ : null }));
}

function loadSchedule() {
  try {
    const saved = JSON.parse(localStorage.getItem(LS_KEY));
    return saved ? assignPeriodNumbers(saved) : assignPeriodNumbers(DEFAULT_SCHEDULE);
  } catch { return assignPeriodNumbers(DEFAULT_SCHEDULE); }
}

export default function Timetable() {
  const { timetable, classes, subjects, staff, saveTimetable, updateTimetable, deleteTimetable } = useApp();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const teachingStaff = useMemo(() =>
    staff.filter(s => !['Driver','Support Staff'].includes(s.designation)), [staff]);

  const defaultTeacher = currentUser?.role === 'teacher'
    ? (currentUser.profileId || '')
    : (teachingStaff[0]?.id || '');

  const [view,            setView]            = useState('class');
  const [selectedClass,   setSelectedClass]   = useState(classes[0]?.id || '');
  const [selectedTeacher, setSelectedTeacher] = useState(defaultTeacher);
  const [schedule,        setSchedule]        = useState(loadSchedule);

  // Period config modal
  const [configOpen,    setConfigOpen]    = useState(false);
  const [editSchedule,  setEditSchedule]  = useState([]);

  // Slot assignment modal
  const [slotModal, setSlotModal] = useState(null);
  const [slotForm,  setSlotForm]  = useState({ subjectId: '', teacherId: '' });

  const subjectColorMap = useMemo(() =>
    subjects.reduce((acc, s, i) => ({ ...acc, [s.id]: SUBJECT_COLORS[i % SUBJECT_COLORS.length] }), {}),
    [subjects]);

  const getSubjectName = (id) => subjects.find(s => s.id === id)?.name || '—';
  const getTeacherName = (id) => {
    const s = staff.find(s => s.id === id);
    return s ? `${s.firstName} ${s.lastName}` : '—';
  };
  const getClassName = (id) => {
    const c = classes.find(c => c.id === id);
    return c ? `${c.name}-${c.section}` : id;
  };

  // Subjects for selected class
  const classSubjects = useMemo(() => {
    const fc = subjects.filter(s => (s.classIds || []).includes(selectedClass));
    return fc.length > 0 ? fc : subjects;
  }, [subjects, selectedClass]);

  // Teacher availability for a slot
  const getSlotAvailability = useCallback((day, period, excludeClassId) => {
    const busyIds = new Set(
      timetable
        .filter(t => t.day === day && t.period === period && t.classId !== excludeClassId)
        .map(t => t.teacherId)
    );
    return teachingStaff.map(s => ({ ...s, busy: busyIds.has(s.id) }));
  }, [timetable, teachingStaff]);

  const getConflict = useCallback((teacherId, day, period, excludeClassId) => {
    if (!teacherId) return null;
    const conflict = timetable.find(t =>
      t.teacherId === teacherId && t.day === day &&
      t.period === period && t.classId !== excludeClassId
    );
    return conflict ? getClassName(conflict.classId) : null;
  }, [timetable, classes]);

  // Open slot modal
  const openSlot = (day, period, row) => {
    if (!isAdmin) return;
    const existing = timetable.find(t =>
      t.classId === selectedClass && t.day === day && t.period === period);
    setSlotForm({ subjectId: existing?.subjectId || '', teacherId: existing?.teacherId || '' });
    setSlotModal({ day, period, startTime: row.startTime, endTime: row.endTime, existing });
  };

  const saveSlot = async () => {
    const { day, period, startTime, endTime, existing } = slotModal;
    const entry = {
      id: existing?.id || generateId('TT'),
      classId: selectedClass, day, period,
      startTime, endTime,
      subjectId: slotForm.subjectId,
      teacherId: slotForm.teacherId,
    };
    if (existing) await updateTimetable(existing.id, entry);
    else await saveTimetable(entry);
    setSlotModal(null);
  };

  const clearSlot = async () => {
    if (slotModal?.existing) await deleteTimetable(slotModal.existing.id);
    setSlotModal(null);
  };

  // Period config
  const openConfig = () => {
    setEditSchedule(schedule.map(r => ({ ...r })));
    setConfigOpen(true);
  };
  const saveConfig = () => {
    const updated = assignPeriodNumbers(editSchedule);
    setSchedule(updated);
    localStorage.setItem(LS_KEY, JSON.stringify(editSchedule));
    setConfigOpen(false);
  };
  const updateEditRow = (idx, field, val) =>
    setEditSchedule(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  const removeEditRow = (idx) =>
    setEditSchedule(prev => prev.filter((_, i) => i !== idx));
  const addEditRow = (type) => {
    const last = editSchedule[editSchedule.length - 1];
    setEditSchedule(prev => [...prev, {
      id: generateId('R'), type,
      label: type === 'period' ? 'Period' : type === 'break' ? 'Break' : 'Lunch',
      startTime: last?.endTime || '16:00', endTime: last?.endTime || '16:00',
    }]);
  };

  // Availability for current slot modal
  const slotStaff = useMemo(() =>
    slotModal ? getSlotAvailability(slotModal.day, slotModal.period, selectedClass) : teachingStaff,
    [slotModal, getSlotAvailability, selectedClass, teachingStaff]);

  const conflict = slotModal
    ? getConflict(slotForm.teacherId, slotModal.day, slotModal.period, selectedClass)
    : null;

  return (
    <div>
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Timetable</h1>
          <p className="text-gray-500 text-sm">Weekly class schedule</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* View toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {[['class','Class-wise'],['teacher','Teacher-wise']].map(([v,l]) => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view===v ? 'bg-white shadow text-purple-700' : 'text-gray-500 hover:text-gray-700'}`}>
                {l}
              </button>
            ))}
          </div>

          {/* Selector */}
          {view === 'class' ? (
            <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
              {classes.map(c => <option key={c.id} value={c.id}>{c.name} – {c.section}</option>)}
            </select>
          ) : (
            <select value={selectedTeacher} onChange={e => setSelectedTeacher(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
              {teachingStaff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
            </select>
          )}

          {isAdmin && (
            <button onClick={openConfig}
              className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors">
              <Settings size={14} /> Configure Periods
            </button>
          )}
        </div>
      </div>

      {/* ── Class-wise grid ── */}
      {view === 'class' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-3 py-3 text-left font-semibold w-36 whitespace-nowrap">Time</th>
                {DAYS.map(d => (
                  <th key={d} className="px-2 py-3 text-center font-semibold min-w-[120px]">{d}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {schedule.map(row => {
                // Break / Lunch row
                if (row.type !== 'period') {
                  const isLunch = row.type === 'lunch';
                  return (
                    <tr key={row.id} className={isLunch ? 'bg-orange-50' : 'bg-gray-50'}>
                      <td className="px-3 py-2">
                        <div className={`text-xs font-semibold ${isLunch ? 'text-orange-500' : 'text-gray-400'}`}>
                          {isLunch ? '🍽 Lunch' : '☕ Break'}
                        </div>
                        <div className="text-xs text-gray-400">{row.startTime}–{row.endTime}</div>
                      </td>
                      {DAYS.map(d => (
                        <td key={d} className="px-2 py-1.5 text-center">
                          <span className={`text-xs ${isLunch ? 'text-orange-300' : 'text-gray-300'}`}>
                            {row.label}
                          </span>
                        </td>
                      ))}
                    </tr>
                  );
                }

                // Period row
                return (
                  <tr key={row.id} className="hover:bg-gray-50/50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="font-semibold text-xs text-gray-700">P{row.number}</div>
                      <div className="text-xs text-gray-400">{row.startTime}–{row.endTime}</div>
                    </td>
                    {DAYS.map(d => {
                      const slot = timetable.find(t =>
                        t.classId === selectedClass && t.day === d && t.period === row.number);
                      const color = slot ? (subjectColorMap[slot.subjectId] || 'bg-gray-100 text-gray-700') : '';
                      return (
                        <td key={d} className="px-1.5 py-1.5 text-center">
                          {slot ? (
                            <div
                              onClick={() => openSlot(d, row.number, row)}
                              className={`${color} rounded-lg px-2 py-1.5 text-xs ${isAdmin ? 'cursor-pointer hover:opacity-75 transition-opacity' : ''}`}
                            >
                              <div className="font-semibold leading-tight">{getSubjectName(slot.subjectId)}</div>
                              <div className="opacity-70 truncate mt-0.5">{getTeacherName(slot.teacherId)}</div>
                            </div>
                          ) : isAdmin ? (
                            <button
                              onClick={() => openSlot(d, row.number, row)}
                              className="w-full h-11 rounded-lg border-2 border-dashed border-gray-200 text-gray-300 hover:border-purple-300 hover:text-purple-400 transition-colors flex items-center justify-center"
                            >
                              <Plus size={15} />
                            </button>
                          ) : (
                            <span className="text-gray-200 text-xs">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Teacher-wise grid ── */}
      {view === 'teacher' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          {teachingStaff.length === 0 ? (
            <div className="p-12 text-center text-gray-400">No teaching staff found</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-3 py-3 text-left font-semibold w-36">Time</th>
                  {DAYS.map(d => (
                    <th key={d} className="px-2 py-3 text-center font-semibold min-w-[120px]">{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {schedule.map(row => {
                  if (row.type !== 'period') {
                    const isLunch = row.type === 'lunch';
                    return (
                      <tr key={row.id} className={isLunch ? 'bg-orange-50' : 'bg-gray-50'}>
                        <td className="px-3 py-2">
                          <div className={`text-xs font-semibold ${isLunch ? 'text-orange-500' : 'text-gray-400'}`}>
                            {isLunch ? '🍽 Lunch' : '☕ Break'}
                          </div>
                          <div className="text-xs text-gray-400">{row.startTime}–{row.endTime}</div>
                        </td>
                        {DAYS.map(d => (
                          <td key={d} className="px-2 py-1.5 text-center">
                            <span className="text-xs text-gray-300">{row.label}</span>
                          </td>
                        ))}
                      </tr>
                    );
                  }
                  return (
                    <tr key={row.id} className="hover:bg-gray-50/50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="font-semibold text-xs text-gray-700">P{row.number}</div>
                        <div className="text-xs text-gray-400">{row.startTime}–{row.endTime}</div>
                      </td>
                      {DAYS.map(d => {
                        const slot = timetable.find(t =>
                          t.teacherId === selectedTeacher && t.day === d && t.period === row.number);
                        return (
                          <td key={d} className="px-1.5 py-1.5 text-center">
                            {slot ? (
                              <div className="bg-blue-50 text-blue-800 border border-blue-100 rounded-lg px-2 py-1.5 text-xs">
                                <div className="font-semibold leading-tight">{getSubjectName(slot.subjectId)}</div>
                                <div className="opacity-70 mt-0.5">{getClassName(slot.classId)}</div>
                              </div>
                            ) : (
                              <span className="text-gray-200 text-xs">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Slot Assignment Modal ── */}
      <Modal
        isOpen={!!slotModal}
        onClose={() => setSlotModal(null)}
        title={slotModal ? `${slotModal.day} · Period ${slotModal.period} · ${slotModal.startTime}–${slotModal.endTime}` : ''}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Subject</label>
            <select
              value={slotForm.subjectId}
              onChange={e => setSlotForm(f => ({ ...f, subjectId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            >
              <option value="">Select Subject</option>
              {classSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">
              Teacher
              <span className="text-xs font-normal text-gray-400 ml-2">✓ Available &nbsp; ⚠ Busy in another class</span>
            </label>
            <select
              value={slotForm.teacherId}
              onChange={e => setSlotForm(f => ({ ...f, teacherId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            >
              <option value="">Select Teacher</option>
              {slotStaff.map(s => (
                <option key={s.id} value={s.id}>
                  {s.busy ? '⚠' : '✓'} {s.firstName} {s.lastName}
                  {s.designation ? ` (${s.designation})` : ''}
                </option>
              ))}
            </select>

            {conflict && (
              <div className="mt-2 flex items-center gap-2 text-amber-700 text-xs bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
                <AlertTriangle size={13} className="flex-shrink-0" />
                <span>
                  <strong>{getTeacherName(slotForm.teacherId)}</strong> already has a class
                  in <strong>{conflict}</strong> during this period. You can still save, but this is a conflict.
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-5 pt-4 border-t">
          {slotModal?.existing ? (
            <button onClick={clearSlot}
              className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-700 transition-colors">
              <Trash2 size={13} /> Clear Slot
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={() => setSlotModal(null)} className="px-4 py-1.5 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
            <button
              onClick={saveSlot}
              disabled={!slotForm.subjectId || !slotForm.teacherId}
              className="px-4 py-1.5 text-sm text-white rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Period Config Modal ── */}
      <Modal isOpen={configOpen} onClose={() => setConfigOpen(false)} title="Configure Period Schedule" size="lg">
        <p className="text-sm text-gray-500 mb-4">
          Define the daily timetable structure — periods, breaks, and lunch. Changes apply to all classes.
        </p>

        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          <div className="grid grid-cols-[90px_130px_1fr_1fr_32px] gap-2 px-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
            <span>Type</span><span>Label</span><span>Start</span><span>End</span><span></span>
          </div>
          {editSchedule.map((row, idx) => (
            <div key={row.id} className="grid grid-cols-[90px_130px_1fr_1fr_32px] gap-2 items-center">
              <select
                value={row.type}
                onChange={e => updateEditRow(idx, 'type', e.target.value)}
                className={`px-2 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-purple-300
                  ${row.type==='break' ? 'border-gray-300 text-gray-600' : row.type==='lunch' ? 'border-orange-200 text-orange-700 bg-orange-50' : 'border-purple-200 text-purple-700 bg-purple-50'}`}
              >
                <option value="period">Period</option>
                <option value="break">Break</option>
                <option value="lunch">Lunch</option>
              </select>
              <input
                type="text"
                value={row.label}
                onChange={e => updateEditRow(idx, 'label', e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-purple-300"
              />
              <input
                type="time"
                value={row.startTime}
                onChange={e => updateEditRow(idx, 'startTime', e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-purple-300"
              />
              <input
                type="time"
                value={row.endTime}
                onChange={e => updateEditRow(idx, 'endTime', e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-purple-300"
              />
              <button onClick={() => removeEditRow(idx)} className="p-1 hover:bg-red-50 rounded text-red-400 transition-colors">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 mt-4">
          <button onClick={() => addEditRow('period')}
            className="text-xs px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 font-medium">
            + Period
          </button>
          <button onClick={() => addEditRow('break')}
            className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 font-medium">
            + Break
          </button>
          <button onClick={() => addEditRow('lunch')}
            className="text-xs px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 font-medium">
            + Lunch
          </button>
          <button onClick={() => setEditSchedule(DEFAULT_SCHEDULE.map(r => ({ ...r })))}
            className="text-xs px-3 py-1.5 text-gray-400 hover:text-gray-600 ml-auto">
            Reset to Default
          </button>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={() => setConfigOpen(false)} className="px-4 py-1.5 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={saveConfig} className="px-4 py-1.5 text-sm text-white bg-purple-600 hover:bg-purple-700 rounded-lg">
            Save Schedule
          </button>
        </div>
      </Modal>
    </div>
  );
}
